import { APP_NAME } from '@repo/shared_utils/constants'
import { createFileRoute } from '@tanstack/react-router'
import { AlertTriangle, Monitor, Smartphone, Trash2 } from 'lucide-react'
import LandingPageWrapper from '../components/landing/landing-page-wrapper'

export const Route = createFileRoute('/account-deletion')({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <LandingPageWrapper>
      <div className="max-w-4xl mx-auto space-y-12 py-12">
        {/* Header */}
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <div className="p-4 bg-red-50 dark:bg-red-950/20 rounded-full">
              <Trash2 className="h-12 w-12 text-red-600" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
            <span className="bg-gradient-to-r from-red-600 via-orange-600 to-red-700 bg-clip-text text-transparent">
              Account Deletion
            </span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Request permanent deletion of your {APP_NAME} account and associated data
          </p>
        </div>

        {/* Warning Section */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 border border-red-200 dark:border-red-800 rounded-2xl p-8">
          <div className="flex items-start gap-4">
            <AlertTriangle className="h-8 w-8 text-red-600 flex-shrink-0 mt-1" />
            <div className="space-y-3">
              <h2 className="text-2xl font-bold text-red-900 dark:text-red-100">
                Important: This Action Cannot Be Undone
              </h2>
              <p className="text-red-800 dark:text-red-200 leading-relaxed">
                Account deletion is permanent and irreversible. Please carefully review what data
                will be deleted before proceeding.
              </p>
            </div>
          </div>
        </div>

        {/* How to Delete Section */}
        <div className="space-y-8">
          <h2 className="text-3xl font-bold text-center">How to Request Account Deletion</h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Mobile App Instructions */}
            <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-blue-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                  <Smartphone className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold">Mobile App</h3>
              </div>

              <div className="space-y-4">
                <p className="text-muted-foreground mb-6">
                  Follow these steps in the {APP_NAME} mobile app:
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                      1
                    </div>
                    <p>
                      Open the app and navigate to your <strong>Dashboard</strong>
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                      2
                    </div>
                    <p>
                      Tap on <strong>Settings</strong>
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                      3
                    </div>
                    <p>
                      Scroll to the <strong>very bottom</strong> of the settings page
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                      4
                    </div>
                    <p>
                      Tap <strong>"Request Account Deletion"</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Web App Instructions */}
            <div className="bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-green-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                  <Monitor className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold">Web App</h3>
              </div>

              <div className="space-y-4">
                <p className="text-muted-foreground mb-6">
                  Follow these steps on the {APP_NAME} website:
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                      1
                    </div>
                    <p>
                      Go to your <strong>Dashboard</strong>
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                      2
                    </div>
                    <p>
                      Click on the <strong>button at the lower right</strong>
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                      3
                    </div>
                    <p>
                      Click on your <strong>Google profile</strong>
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                      4
                    </div>
                    <p>
                      Click <strong>"Delete My Account"</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Data Deletion Information */}
        <div className="bg-gradient-to-br from-gray-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-slate-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <h2 className="text-3xl font-bold mb-6 text-center">What Data Gets Deleted</h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-red-600">
                Data That Will Be Permanently Deleted:
              </h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  Your user profile and account information
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  Personal preferences and settings
                </li>

                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  Employee information you've entered
                </li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-amber-600">Data That May Be Retained:</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Anonymized analytics data (for service improvement)
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Legal compliance records (as required by law)
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Transaction records for tax purposes (7 years)
                </li>
              </ul>

              <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Retention Period:</strong> Some financial and tax-related records may be
                  retained for up to 7 years as required by Philippine tax and labor laws.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20 border border-indigo-200 dark:border-indigo-800 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Need Help?</h2>
          <p className="text-muted-foreground mb-6">
            If you have questions about account deletion or need assistance, please contact our
            support team.
          </p>
          <p className="text-lg">
            Email:{' '}
            <a
              href="mailto:cubic.payroll.101@gmail.com"
              className="text-indigo-600 hover:text-indigo-700 font-medium"
            >
              cubic.payroll.101@gmail.com
            </a>
          </p>
        </div>
      </div>
    </LandingPageWrapper>
  )
}
